import React, { useEffect, useMemo, useRef, useState } from 'react';
import API from '../api';
import Layout from './Layout';
import { useTranslation } from 'react-i18next';

const EMPTY_FORM = {
  name: '',
  phone: '',
  phone2: '',
  email: '',
  address: '',
  zone: '',
  whatsapp_number: '',
  notes: '',
};

export default function Customers() {
  const { t } = useTranslation();
  const [allCustomers, setAllCustomers] = useState([]);
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const formCardRef = useRef(null);

  const load = async () => {
    const res = await API.get('customers/').catch(() => ({ data: [] }));
    setAllCustomers(Array.isArray(res.data) ? res.data : []);
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (showForm) {
      formCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [showForm, editing]);

  const filtered = useMemo(() => {
    return allCustomers.filter((c) => {
      const text = `${c.name} ${c.phone} ${c.phone2 || ''} ${c.customer_code || ''} ${c.address || ''} ${c.zone || ''}`.toLowerCase();
      return text.includes(query.toLowerCase());
    });
  }, [allCustomers, query]);

  const customerSuggestions = useMemo(() => {
    const q = query.toLowerCase().trim();
    const base = q
      ? allCustomers.filter((c) => `${c.name} ${c.phone} ${c.customer_code || ''}`.toLowerCase().includes(q))
      : allCustomers;
    return base.slice(0, 25).map((c) => `${c.name} - ${c.phone} - ${c.customer_code || ''}`);
  }, [allCustomers, query]);

  const save = async (e) => {
    e.preventDefault();
    if (editing) {
      await API.put(`customers/${editing.id}/`, form).catch(() => {});
    } else {
      await API.post('customers/', form).catch(() => {});
    }
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowForm(false);
    load();
  };

  const openEdit = (customer) => {
    setEditing(customer);
    setShowForm(true);
    setForm({
      name: customer.name || '',
      phone: customer.phone || '',
      phone2: customer.phone2 || '',
      email: customer.email || '',
      address: customer.address || '',
      zone: customer.zone || '',
      whatsapp_number: customer.whatsapp_number || '',
      notes: customer.notes || '',
    });
  };

  const remove = async (customer) => {
    const confirmed = window.confirm(t('Are you sure you want to delete this customer?'));
    if (!confirmed) return;
    await API.delete(`customers/${customer.id}/`).catch((err) => {
      const msg = err?.response?.data?.error || t('Delete failed');
      window.alert(msg);
    });
    load();
  };

  return (
    <Layout title={t('Customers')}>
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="filters-row">
          <input
            className="input"
            list="customers-search-suggestions"
            placeholder={t('Advanced search customers')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <datalist id="customers-search-suggestions">
            {customerSuggestions.map((item) => (
              <option key={item} value={item} />
            ))}
          </datalist>
          <button
            className="btn btn-primary"
            type="button"
            onClick={() => {
              setEditing(null);
              setForm(EMPTY_FORM);
              setShowForm((prev) => !prev);
            }}
          >
            {showForm && !editing ? t('Hide Add Form') : t('Add Customer')}
          </button>
        </div>
      </div>

      {showForm && (
        <div className="card" style={{ marginBottom: 16 }} ref={formCardRef}>
          <h3 style={{ marginBottom: 10 }}>{editing ? t('Edit Customer') : t('Add Customer')}</h3>
          <form onSubmit={save} className="form-grid">
            <input className="input" placeholder={t('Name')} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            <input className="input" placeholder={t('Phone')} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required />
            <input className="input" placeholder={t('Phone 2')} value={form.phone2} onChange={(e) => setForm({ ...form, phone2: e.target.value })} />
            <input className="input" placeholder={t('WhatsApp')} value={form.whatsapp_number} onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })} />
            <input className="input" placeholder={t('Email')} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <input className="input" placeholder={t('Zone')} value={form.zone} onChange={(e) => setForm({ ...form, zone: e.target.value })} />
            <textarea className="input" placeholder={t('Address')} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            <textarea className="input" placeholder={t('Notes')} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            <div className="row-actions">
              <button className="btn btn-primary" type="submit">{editing ? t('Update') : t('Create')}</button>
              <button className="btn btn-secondary" type="button" onClick={() => { setEditing(null); setForm(EMPTY_FORM); setShowForm(false); }}>{t('Cancel')}</button>
            </div>
          </form>
        </div>
      )}

      <div className="card table-wrap">
        <table>
          <thead>
            <tr>
              <th>{t('Code')}</th>
              <th>{t('Name')}</th>
              <th>{t('Phone')}</th>
              <th>{t('Address')}</th>
              <th>{t('Actions')}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c.id}>
                <td>{c.customer_code}</td>
                <td>{c.name}</td>
                <td>{c.phone}</td>
                <td>{c.address || '-'}</td>
                <td>
                  <div className="row-actions">
                    <button className="btn btn-secondary" type="button" onClick={() => openEdit(c)}>{t('Edit')}</button>
                    <button className="btn btn-danger" type="button" onClick={() => remove(c)}>{t('Delete')}</button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={5}>{t('No customers found')}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Layout>
  );
}
